import { useCallback, useEffect, useReducer, useRef } from "react";
import qapi from "views_shared/app/javascript/api";
import useViewerReceipts from "../../../realtime_subscriptions/useViewerReceipts";

// interact_conversation_receivers_mat may not be refreshed yet when the receipt event lands; give
// the mat refresh a beat before refetching.
const MAT_REFRESH_GRACE_MS = 250;

const initialState = {
  // { [origin_type]: { [origin_id]: last_sent_at (ISO string) } }
  lastMessageIndex: {},
  // { [co_participant_profile_id]: last_sent_at (ISO string) } — peer (profile-to-profile) DMs,
  // for source cards messaged profile-to-profile (PR profiles) that have no promo-entity origin.
  peerMessageIndex: {},
  isLoading: true,
  error: null,
};

// Rows arrive ordered last_sent_at DESC, so the first row seen per (origin_type, origin_id) is
// already the most recent activity — keep it and ignore later (older) duplicates. This gives
// MAX(last_sent_at) per origin without comparing timestamps while folding.
function buildLastMessageIndex(rows) {
  return rows.reduce((acc, row) => {
    const type = row.origin_type;
    const id = row.origin_id;
    if (acc[type]?.[id] !== undefined) return acc;
    return {
      ...acc,
      [type]: {
        ...(acc[type] || {}),
        [id]: row.last_sent_at,
      },
    };
  }, {});
}

// Peer rows arrive last_sent_at DESC too, so the first time a co-participant id is seen it is
// already their most recent activity — keep it, ignore later (older) rows. A single conversation
// can list more than one co-participant, so fold the whole array of each row.
function buildPeerMessageIndex(rows) {
  return rows.reduce((acc, row) => {
    (row.co_participant_profile_ids || []).forEach((id) => {
      if (acc[id] === undefined) acc[id] = row.last_sent_at;
    });
    return acc;
  }, {});
}

function reducer(state, action) {
  switch (action.type) {
    case "LOADED":
      return {
        ...state,
        lastMessageIndex: buildLastMessageIndex(action.rows),
        peerMessageIndex: buildPeerMessageIndex(action.peerRows),
        isLoading: false,
        error: null,
      };
    case "ERROR":
      return { ...state, isLoading: false, error: action.error };
    default:
      return state;
  }
}

export default function useLastMessages() {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Guards every dispatch so a load() that resolves after unmount can't touch a stale reducer.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const load = useCallback(async () => {
    const profileId = globalThis.Qwoted?.CURRENT_PROFILE_ID;
    // Anonymous visitors (no user) and users without an active profile have no conversations to
    // key against — settle into the loaded/empty state without hitting the API.
    if (!globalThis.Qwoted?.CURRENT_USER_ID || !profileId) {
      if (mountedRef.current) dispatch({ type: "LOADED", rows: [], peerRows: [] });
      return;
    }

    const [origin, peer] = await Promise.all([
      qapi.conversations.fetchLastMessageIndex(profileId).catch((e) => ({ error: e })),
      qapi.conversations.fetchPeerLastMessageIndex(profileId).catch((e) => ({ error: e })),
    ]);

    if (!mountedRef.current) return;

    const error = origin.error || peer.error;
    if (error) {
      dispatch({ type: "ERROR", error });
      return;
    }

    dispatch({ type: "LOADED", rows: origin.data || [], peerRows: peer.data || [] });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the index live: refetch when a receipt for the viewer changes (their own sends included),
  // and on reconnect/tab-resume — so a card re-surfaced by a later search shows the current date,
  // not the date captured at first page load.
  useViewerReceipts(
    useCallback(
      (event) => {
        if (event.type === "receipt") {
          setTimeout(load, MAT_REFRESH_GRACE_MS);
        } else {
          load();
        }
      },
      [load],
    ),
  );

  return { state };
}
